import type {FC} from 'react'
import type React from 'react'
import {useMemo, useState, Fragment, useCallback, useRef} from 'react'
import type {RulesetTarget, TargetType, IncludeExcludeParameters} from '@github-ui/repos-rules-types/rules-types'
import {INCLUDE_ALL_PATTERN, DEFAULT_BRANCH_PATTERN} from '@github-ui/repos-rules-types/rules-types'
import {TargetsTable, type IncludeExcludeType} from '../TargetsTable'
import {ActionList, ActionMenu, FormControl, Checkbox} from '@primer/react'
import {PlusCircleIcon, XCircleIcon} from '@primer/octicons-react'
import {pluralize} from '../../../helpers/string'
import {PLURAL_RULESET_TARGETS} from '../../../helpers/constants'
import {AddTargetDialog} from './AddTargetDialog'

import styles from './IncludeExcludeTarget.module.css'

function targetPrefix(targetType: TargetType): string | undefined {
  if (targetType === 'ref_name') {
    return undefined
  }

  return targetType === 'workflow_path' ? 'path' : 'name'
}

interface IncludeExcludeTargetProps {
  rulesetId?: number
  rulesetTarget: RulesetTarget
  fnmatchHelpUrl?: string
  parameters: IncludeExcludeParameters
  panelTitle: string
  targetType: TargetType
  readOnly: boolean
  updateParameters: (parameters: IncludeExcludeParameters) => void
  headerRowText?: string
  implicitAllLabel?: string
  blankslate: {
    heading: string
    description?: React.ReactNode
  }
  customActions?: IncludeExcludeTargetCustomAction[]
  // React preserves in-progress dialog state only while this component type remains referentially stable.
  renderCustomDialog?: React.ComponentType<IncludeExcludeTargetCustomDialogProps>
  getTargetPresentation?: (target: {type: IncludeExcludeType; value: string}) => {
    prefix?: string
    display?: string | string[]
    customDisplay?: React.ReactNode
    displayAsLabel?: boolean
    displayAsCode?: boolean
    removeLabel?: string
  }
}

export type IncludeExcludeTargetCustomDialogProps = {
  action: IncludeExcludeTargetCustomAction
  onAdd: (target: string) => void
  onClose: () => void
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
}

export type IncludeExcludeTargetCustomAction = {
  type: string
  text: string
  actionGroup?: string
  includeOrExclude: IncludeExcludeType
}

const EMPTY_CUSTOM_ACTIONS: IncludeExcludeTargetCustomAction[] = []

export const IncludeExcludeTarget: FC<IncludeExcludeTargetProps> = ({
  rulesetId,
  readOnly,
  rulesetTarget,
  fnmatchHelpUrl,
  parameters,
  panelTitle,
  targetType,
  updateParameters,
  headerRowText,
  implicitAllLabel,
  blankslate,
  customActions = EMPTY_CUSTOM_ACTIONS,
  renderCustomDialog,
  getTargetPresentation,
}) => {
  const [selectedActionType, setSelectedActionType] = useState('include_target')
  const [addTargetVisible, setAddTargetVisible] = useState(false)
  const addTargetButtonRef = useRef<HTMLButtonElement>(null)
  const removeButtonsRef = useRef(new Map<string, HTMLButtonElement>())
  const implicitPattern = implicitAllLabel === undefined ? undefined : INCLUDE_ALL_PATTERN
  const CustomDialog = renderCustomDialog

  const actions: Action[] = [
    ...actionOptions(rulesetTarget, targetType),
    ...customActions.map(action => ({...action, custom: true})),
  ]
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentAction = actions.find(option => option.type === selectedActionType) || actions[0]!

  let allLabel: string | undefined

  switch (targetType) {
    case 'ref_name':
      allLabel = `All ${pluralize(2, rulesetTarget, PLURAL_RULESET_TARGETS, false)}`
      break
    case 'repository_name':
      allLabel = `All repositories`
      break
    case 'organization_name':
      allLabel = `All organizations`
      break
  }

  const toggleProtectedTarget = () => {
    updateParameters({
      ...parameters,
      protected: !parameters.protected,
    })
  }

  const onActionSelect = (actionType: string) => {
    setSelectedActionType(actionType)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const action = actions.find(option => option.type === actionType) || actions[0]!

    if (action.target === undefined) {
      setAddTargetVisible(true)
    } else {
      addParameter('', action)
    }
  }

  const addParameter = (param: string, action = currentAction) => {
    if (readOnly) {
      return
    }

    let target = action.target ?? param.trim()
    if (!target) {
      return
    }

    if (action.target === undefined && targetType === 'ref_name') {
      switch (rulesetTarget) {
        case 'branch':
          target = `refs/heads/${target}`
          break
        case 'tag':
          target = `refs/tags/${target}`
          break
      }
    }

    const existingTargets =
      action.target === undefined
        ? parameters.include.filter(pattern => pattern !== implicitPattern).concat(parameters.exclude)
        : parameters.include
    if (existingTargets.includes(target)) {
      return
    }

    const parameterType = action.includeOrExclude
    updateParameters({...parameters, [parameterType]: [...parameters[parameterType], target]})
  }

  const mapAliasToDisplay = useCallback(
    (value: string) => {
      if (value === INCLUDE_ALL_PATTERN) {
        return implicitAllLabel ?? allLabel
      } else if (targetType === 'ref_name' && value === DEFAULT_BRANCH_PATTERN) {
        return 'Default'
      } else {
        return undefined
      }
    },
    [allLabel, targetType, implicitAllLabel],
  )

  const targets = useMemo(() => {
    return [parameters.include.map(e => ({t: 'include', v: e})), parameters.exclude.map(e => ({t: 'exclude', v: e}))]
      .flat(1)
      .map(param => {
        const type = param.t as IncludeExcludeType
        const presentation = getTargetPresentation?.({type, value: param.v})
        const display = mapAliasToDisplay(param.v)

        return {
          type,
          prefix: presentation?.prefix ?? (display === undefined ? targetPrefix(targetType) : undefined),
          value: param.v,
          display: presentation?.display ?? display,
          customDisplay: presentation?.customDisplay,
          displayAsLabel: presentation?.displayAsLabel ?? display !== undefined,
          displayAsCode: presentation?.displayAsCode ?? (display === undefined && targetType === 'workflow_path'),
          removeLabel: presentation?.removeLabel,
          removable: param.t !== 'include' || param.v !== implicitPattern,
          removeButtonRef: (button: HTMLButtonElement | null) => {
            if (button) {
              const key = `${param.t}/${param.v}`
              removeButtonsRef.current.set(key, button)
              return () => {
                removeButtonsRef.current.delete(key)
              }
            }
          },
        }
      })
  }, [getTargetPresentation, parameters, targetType, mapAliasToDisplay, removeButtonsRef, implicitPattern])

  const removeParameter = (pattern_type: 'include' | 'exclude', target: string) => {
    if (readOnly) {
      return
    }
    const list = (pattern_type === 'include' ? parameters.include : parameters.exclude).filter(p => p !== target)

    const newParameters =
      pattern_type === 'include'
        ? {
            ...parameters,
            include: list,
          }
        : {
            ...parameters,
            exclude: list,
          }

    const removedIndex = targets.findIndex(item => item.type === pattern_type && item.value === target)
    const remainingTargets = targets.filter(item => item.type !== pattern_type || item.value !== target)
    const nextTarget = remainingTargets[removedIndex] ?? remainingTargets.at(-1)
    const nextButton = nextTarget ? removeButtonsRef.current.get(`${nextTarget.type}/${nextTarget.value}`) : undefined
    const focusTarget = nextButton ?? addTargetButtonRef.current
    focusTarget?.focus()

    updateParameters(newParameters)
  }

  const noGroupActions = actions.filter(a => !a.actionGroup)
  const actionsByGroup = actions
    .filter(a => a.actionGroup)
    .reduce((acc, item) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const actionList = acc.get(item.actionGroup!) || []
      actionList.push(item)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acc.set(item.actionGroup!, actionList)
      return acc
    }, new Map<string, Action[]>())

  return (
    <>
      <TargetsTable
        renderTitle={() => <h3 className="Box-title">{panelTitle}</h3>}
        renderAction={() => (
          <ActionMenu anchorRef={addTargetButtonRef}>
            <ActionMenu.Button ref={addTargetButtonRef}>Add target</ActionMenu.Button>

            <ActionMenu.Overlay width="medium">
              <ActionList>
                {noGroupActions.length > 0 &&
                  noGroupActions.map(action => (
                    <ActionList.Item
                      key={action.type}
                      onSelect={() => onActionSelect(action.type)}
                      disabled={action.target ? parameters.include.includes(action.target) : false}
                    >
                      <ActionList.LeadingVisual>
                        {action.includeOrExclude === 'include' ? (
                          <PlusCircleIcon className={styles.IncludeTargetIcon} />
                        ) : (
                          <XCircleIcon className={styles.ExcludeTargetIcon} />
                        )}
                      </ActionList.LeadingVisual>
                      {action.text}
                    </ActionList.Item>
                  ))}
                {Array.from(actionsByGroup).map(([group, actionList], index) => (
                  <Fragment key={group}>
                    {(noGroupActions.length > 0 || index > 0) && <ActionList.Divider />}
                    <ActionList.Group variant="subtle">
                      <ActionList.GroupHeading>{group}</ActionList.GroupHeading>
                      {actionList.map(action => (
                        <ActionList.Item key={action.type} onSelect={() => onActionSelect(action.type)}>
                          <ActionList.LeadingVisual>
                            {action.includeOrExclude === 'include' ? (
                              <PlusCircleIcon className={styles.IncludeTargetIcon} />
                            ) : (
                              <XCircleIcon className={styles.ExcludeTargetIcon} />
                            )}
                          </ActionList.LeadingVisual>
                          {action.text}
                        </ActionList.Item>
                      ))}
                    </ActionList.Group>
                  </Fragment>
                ))}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
        headerRowText={headerRowText}
        blankslate={blankslate}
        targets={targets}
        onRemove={removeParameter}
        readOnly={readOnly}
      />
      {!readOnly && (targetType === 'repository_name' || targetType === 'repository_id') && (
        <div className="tmp-pt-3">
          <FormControl>
            <Checkbox checked={parameters.protected || false} onChange={toggleProtectedTarget} />
            <FormControl.Label>Prevent renaming of target repositories</FormControl.Label>
            <FormControl.Caption>
              When checked, target repositories can only be renamed by those with bypass permission.
            </FormControl.Caption>
          </FormControl>
        </div>
      )}
      {readOnly || !addTargetVisible ? null : currentAction.custom && CustomDialog ? (
        <CustomDialog
          action={currentAction}
          onAdd={target => {
            addParameter(target, currentAction)
            setAddTargetVisible(false)
          }}
          onClose={() => setAddTargetVisible(false)}
          returnFocusRef={addTargetButtonRef}
        />
      ) : (
        <AddTargetDialog
          rulesetId={rulesetId}
          onAdd={target => {
            addParameter(target, currentAction)
            setAddTargetVisible(false)
          }}
          onClose={() => setAddTargetVisible(false)}
          target={targetType}
          includeOrExclude={currentAction.includeOrExclude}
          rulesetTarget={rulesetTarget}
          fnmatchHelpUrl={fnmatchHelpUrl}
          returnFocusRef={addTargetButtonRef}
        />
      )}
    </>
  )
}

type Action = {
  type: string
  text: string
  actionGroup?: string
  target?: string
  includeOrExclude: 'include' | 'exclude'
  custom?: boolean
}

function actionOptions(rulesetTarget: RulesetTarget, targetType: TargetType) {
  const actions: Action[] = []

  if (targetType === 'ref_name') {
    if (rulesetTarget === 'branch') {
      actions.push({
        type: 'include_default_branch',
        text: 'Include default branch',
        includeOrExclude: 'include',
        target: DEFAULT_BRANCH_PATTERN,
      })
    }

    actions.push({
      type: 'include_all',
      text: `Include all ${pluralize(2, rulesetTarget, PLURAL_RULESET_TARGETS, false)}`,
      includeOrExclude: 'include',
      target: INCLUDE_ALL_PATTERN,
    })
  }

  actions.push({
    type: 'include_target',
    text: `Include by pattern`,
    actionGroup: 'Target by inclusion or exclusion pattern',
    includeOrExclude: 'include',
  })

  actions.push({
    type: 'exclude_target',
    text: `Exclude by pattern`,
    actionGroup: 'Target by inclusion or exclusion pattern',
    includeOrExclude: 'exclude',
  })

  return actions
}
